/**
 * Helpers for presenting the opaque `gmailSenderId` shown in the PMS Processing Tool's
 * "Unassigned Property Management Files" table.
 *
 * A `gmailSenderId` is a base64 blob that decodes to a fixed shape:
 *   `[F]<Sender Name>-[T]sync]<per-file noise>`
 * The only human-meaningful part is the sender name; the rest is per-file noise. These
 * helpers extract that name (with a safe fallback) and tally how often each sender recurs.
 */

const SENDER_LABEL_REGEX = /\[F\](.*?)-\[T\]/;

const decodeBase64 = (value) => {
  try {
    if (typeof atob === 'function') {
      return atob(value);
    }
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(value, 'base64').toString('binary');
    }
  } catch (err) {
    return null;
  }
  return null;
};

/**
 * Shorten a long opaque id to a `head…tail` stub so it fits in a table cell.
 */
export const truncateSenderId = (value, head = 8, tail = 6) => {
  if (!value || value.length <= head + tail + 1) {
    return value || '';
  }
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
};

/**
 * Decode a `gmailSenderId` into its human-readable sender name. Falls back to a truncated
 * stub of the raw value when the blob can't be decoded or doesn't match the expected shape.
 * Never throws.
 */
export const decodeGmailSenderLabel = (rawSenderId) => {
  if (!rawSenderId) {
    return '';
  }
  const decoded = decodeBase64(rawSenderId);
  if (decoded) {
    const match = decoded.match(SENDER_LABEL_REGEX);
    if (match && match[1].trim()) {
      return match[1].trim();
    }
  }
  return truncateSenderId(rawSenderId);
};

/**
 * Group a flat list of files into `{ senderId, parent, children, count }` groups keyed by the
 * EXACT raw `gmailSenderId`, preserving input order.
 *
 * We group on the raw, undecoded `gmailSenderId` on purpose: two files can decode to the same
 * human-readable sender name yet carry different per-file noise (different raw ids). Those are NOT
 * duplicates for this tool — "Assign to Partner" maps the raw `gmailSenderId`, so only an exact
 * raw-id match means the files would actually be assigned together.
 *
 * The server returns rows ordered by `gmailSenderId, createdAt desc`, so the first file seen for a
 * given id is the newest and becomes the group's `parent`; the rest become `children`. The PMS
 * Processing Tool shows the parent collapsed (with `count`) and reveals `children` on expand.
 * Files with no `gmailSenderId` each become their own singleton group (never merged together).
 */
export const groupFilesBySenderId = (items = []) => {
  const groups = [];
  const groupIndexBySenderId = {};
  items.forEach((item) => {
    const senderId = item && item.gmailSenderId;
    const existingIndex = senderId ? groupIndexBySenderId[senderId] : undefined;
    if (existingIndex === undefined) {
      if (senderId) {
        groupIndexBySenderId[senderId] = groups.length;
      }
      groups.push({ senderId, parent: item, children: [], count: 1 });
    } else {
      const group = groups[existingIndex];
      group.children.push(item);
      group.count += 1;
    }
  });
  return groups;
};
